import {Box} from '@mui/material';
import {GridColDef, GridValidRowModel} from '@mui/x-data-grid-pro';
import {MOON_250} from '@wandb/weave/common/css/color.styles';
import {Icon} from '@wandb/weave/components/Icon';
import React, {Component, ReactNode} from 'react';

interface CellErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface CellErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component specifically designed for data grid cells.
 * Prevents errors in individual cells from crashing the entire page.
 * Shows a subtle error indicator with tooltip when an error occurs.
 */
class CellErrorBoundary extends Component<
  CellErrorBoundaryProps,
  CellErrorBoundaryState
> {
  constructor(props: CellErrorBoundaryProps) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError(error: Error): CellErrorBoundaryState {
    return {hasError: true, error};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging
    console.error('Cell rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error display: subtle icon with tooltip
      return <CellErrorFallback />;
    }

    return this.props.children;
  }
}

const CellErrorFallback = () => {
  return (
    <Box
      sx={{color: MOON_250, display: 'flex', alignItems: 'center', gap: '4px'}}>
      <Icon
        name="warning"
        style={{
          width: '16px',
          height: '16px',
        }}
      />
      Render error
    </Box>
  );
};

export const protectColumnsFromErrors = <T extends GridValidRowModel>(
  unprotectedColumnDefs: GridColDef<T>[]
): GridColDef<T>[] => {
  return unprotectedColumnDefs.map(colDef => {
    if (!colDef.renderCell) {
      return colDef;
    }
    const renderCell = colDef.renderCell;
    return {
      ...colDef,
      renderCell: params => (
        <CellErrorBoundary>{renderCell(params)}</CellErrorBoundary>
      ),
    };
  });
};
